import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FilePdf, CircleNotch, CreditCard, CheckCircle } from "@phosphor-icons/react";

const money = (n, cur = "USD") => `${cur === "USD" ? "$" : cur + " "}${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceView() {
  const { publicId } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = () => api.get(`/public/invoices/${publicId}`).then((r) => setInv(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [publicId]);

  // After returning from Stripe, confirm payment
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setChecking(true);
    api.get(`/public/invoices/${publicId}/pay-status/${sid}`).then((r) => {
      if (r.data.payment_status === "paid") {
        toast.success("Payment received — thank you!");
        load();
      }
    }).catch(() => {}).finally(() => {
      setChecking(false);
      params.delete("session_id");
      setParams(params, { replace: true });
    });
    // eslint-disable-next-line
  }, [publicId]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/invoices/${publicId}/pay`, { origin_url: window.location.origin });
      if (data.already_paid) { toast.success("This invoice is already paid."); load(); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start payment.");
      setPaying(false);
    }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bk-page)" }}><p style={{ color: "var(--bk-text-2)" }}>This invoice link is invalid or has been removed.</p></div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={28} className="animate-spin" /></div>;

  const cur = inv.currency || "USD";
  const st = { draft: "Draft", sent: "Sent", paid: "Paid" }[inv.status] || "Draft";

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "var(--bk-page)" }}>
      <div className="max-w-2xl mx-auto bk-card p-8 sm:p-10" data-testid="public-invoice">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{inv.business_name}</p>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Invoice {inv.number}</p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-accent)" }}>INVOICE</p>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full inline-block mt-1" style={{ background: inv.status === "paid" ? "var(--bk-success-bg)" : "var(--bk-page)", color: inv.status === "paid" ? "#2F6B4F" : "var(--bk-text-2)" }}>{st}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-8 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Bill to</p>
            <p className="font-semibold mt-1" style={{ color: "var(--bk-text)" }}>{inv.client_name}</p>
            {inv.client_email && <p style={{ color: "var(--bk-text-2)" }}>{inv.client_email}</p>}
          </div>
          <div className="text-right">
            <p style={{ color: "var(--bk-text-2)" }}>Issued: <span style={{ color: "var(--bk-text)" }}>{inv.issue_date}</span></p>
            <p style={{ color: "var(--bk-text-2)" }}>Due: <span style={{ color: "var(--bk-text)" }}>{inv.due_date || "—"}</span></p>
          </div>
        </div>

        <table className="w-full mt-8 text-sm">
          <thead>
            <tr style={{ borderBottom: "2px solid var(--bk-border)" }}>
              <th className="text-left py-2" style={{ color: "var(--bk-text-2)" }}>Description</th>
              <th className="text-right py-2" style={{ color: "var(--bk-text-2)" }}>Qty</th>
              <th className="text-right py-2" style={{ color: "var(--bk-text-2)" }}>Rate</th>
              <th className="text-right py-2" style={{ color: "var(--bk-text-2)" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, i) => (
              <tr key={i} style={{ borderBottom: "1px solid var(--bk-border)" }}>
                <td className="py-2" style={{ color: "var(--bk-text)" }}>{it.description}</td>
                <td className="py-2 text-right" style={{ color: "var(--bk-text)" }}>{it.quantity}</td>
                <td className="py-2 text-right" style={{ color: "var(--bk-text)" }}>{money(it.rate, cur)}</td>
                <td className="py-2 text-right font-semibold" style={{ color: "var(--bk-text)" }}>{money(it.amount, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-4">
          <div className="w-56 text-sm">
            <div className="flex justify-between py-1"><span style={{ color: "var(--bk-text-2)" }}>Subtotal</span><span style={{ color: "var(--bk-text)" }}>{money(inv.subtotal, cur)}</span></div>
            {inv.discount_amount > 0 && (
              <div className="flex justify-between py-1" data-testid="inv-view-discount"><span style={{ color: "var(--bk-success)" }}>Discount ({inv.discount_code})</span><span style={{ color: "var(--bk-success)" }}>-{money(inv.discount_amount, cur)}</span></div>
            )}
            <div className="flex justify-between py-1"><span style={{ color: "var(--bk-text-2)" }}>Tax ({inv.tax_rate}%)</span><span style={{ color: "var(--bk-text)" }}>{money(inv.tax_amount, cur)}</span></div>
            <div className="flex justify-between py-2 mt-1 border-t font-display text-lg font-bold" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-text)" }}><span>Total</span><span>{money(inv.total, cur)}</span></div>
          </div>
        </div>

        {inv.notes && <p className="text-sm mt-6" style={{ color: "var(--bk-text-2)" }}><span className="font-semibold">Notes: </span>{inv.notes}</p>}

        <div className="flex flex-wrap items-center gap-3 mt-8">
          {inv.status === "paid" ? (
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold" style={{ background: "var(--bk-success-bg)", color: "#2F6B4F" }} data-testid="invoice-paid-badge">
              <CheckCircle size={18} weight="fill" /> Paid — thank you!
            </span>
          ) : (
            <button onClick={pay} disabled={paying || checking} data-testid="pay-invoice-btn" className="bk-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold disabled:opacity-70">
              {paying || checking ? <><CircleNotch size={16} className="animate-spin" /> {checking ? "Confirming payment…" : "Redirecting…"}</> : <><CreditCard size={18} weight="bold" /> Pay {money(inv.total, cur)}</>}
            </button>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/invoices/${publicId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="public-invoice-pdf" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold border hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            <FilePdf size={16} weight="bold" /> Download PDF
          </a>
        </div>
        <p className="text-xs mt-6" style={{ color: "var(--bk-text-2)" }}>Powered by IronRoot</p>
      </div>
    </div>
  );
}
